import { deriveCacheState } from './deriveCacheState'
import { filterResources } from './filterResources'
import { printDebugInfo } from './printDebugInfo'

export type CacheState = 'cold' | 'mixed' | 'warm' | 'unknown'

interface CacheStateResult {
  cacheState: CacheState
  encodedBytes: number
  transferredBytes: number
  consideredCount: number
  fetchedCount: number
  notFetchedCount: number
}

interface ComputeCacheStateForScriptsArgs {
  onlySameOrigin?: boolean
  pathPrefixes?: string[]
  debug?: boolean
}

export const computeCacheStateForScripts = ({
  onlySameOrigin = true,
  pathPrefixes = [],
  debug = false,
}: ComputeCacheStateForScriptsArgs = {}): CacheStateResult => {
  const empty: CacheStateResult = {
    cacheState: 'unknown',
    encodedBytes: 0,
    transferredBytes: 0,
    consideredCount: 0,
    fetchedCount: 0,
    notFetchedCount: 0,
  }

  const isBrowser = typeof window !== 'undefined'
  const hasPerformance = typeof performance !== 'undefined'
  const hasGetEntries = hasPerformance && typeof performance.getEntriesByType === 'function'
  const isEnvironmentSupported = isBrowser && hasPerformance && hasGetEntries

  if (!isEnvironmentSupported) {
    return empty
  }

  const entries = performance.getEntriesByType('resource')
  const resources = entries.filter((entry): entry is PerformanceResourceTiming => 'initiatorType' in entry)
  const origin = window.location.origin
  const scripts = filterResources(resources, onlySameOrigin, origin, pathPrefixes)

  if (scripts.length === 0) {
    return empty
  }

  const rows = scripts.map(({ name, encodedBodySize, transferSize }) => {
    let path = name
    try {
      path = new URL(name, window.location.href).pathname
    } catch {}

    return {
      path,
      encodedBodySize: encodedBodySize ?? 0,
      transferSize: transferSize ?? 0,
    }
  })

  const encodedBytes = rows.reduce((sum, row) => sum + row.encodedBodySize, 0)
  const transferredBytes = rows.reduce((sum, row) => sum + row.transferSize, 0)
  const fetchedCount = rows.filter((row) => row.transferSize > 0).length
  const notFetchedCount = rows.length - fetchedCount
  const cacheState = deriveCacheState(rows.length, fetchedCount)

  if (debug) {
    printDebugInfo({
      cacheState,
      onlySameOrigin,
      pathPrefixes,
      rows,
      encodedBytes,
      transferredBytes,
    })
  }

  return {
    cacheState,
    encodedBytes,
    transferredBytes,
    consideredCount: rows.length,
    fetchedCount,
    notFetchedCount,
  }
}
